<script lang="ts" setup>
import type { ComputedRef } from "vue"
import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import { useField } from "vee-validate"
import { computed, ref, toRef } from "vue"
import { Hint, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"

interface InputProps {
  id?: string
  name: string
  label?: string
  modelValue?: string
  error?: string
  hint?: string
  type?: string
  disabled?: boolean
  clearable?: boolean
  noBorder?: boolean
  labelIcon?: IconKeys
  required?: boolean
  placeholder?: string
}

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<InputProps>(), {
  id: "",
  label: "",
  modelValue: "",
  error: "",
  hint: "",
  type: "text",
  disabled: false,
  clearable: false,
  required: false,
  noBorder: false,
  labelIcon: undefined,
  placeholder: "",
})

const emit = defineEmits<{
  "update:modelValue": [modelValue?: string]
  "clear": [modelValue: undefined]
}>()

const { t } = useTranslation()

const name = toRef(props, "name")

const { value, errorMessage, handleBlur, handleChange, handleReset } = useField(name)

const inputRef = ref<HTMLInputElement | null>(null)
defineExpose({ inputRef })

const contentHidden = ref<boolean>(false)

function onInput(event: Event) {
  const target = event.target as HTMLInputElement
  handleChange(event)
  emit("update:modelValue", target.value)
}

function onClear() {
  handleReset()
  emit("update:modelValue", undefined)
  emit("clear", undefined)
}

const hasErrors: ComputedRef<boolean> = computed(() => {
  return !!errorMessage.value || !!props.error
})

const isLabelFloating = computed(() => value.value || props.modelValue || (props.placeholder && !(value.value || props.modelValue)))

const computedLabel = computed(() => {
  if (!props.required)
    return props.label

  return `${props.label}*`
})
</script>

<template>
  <div>
    <div
      class="gpx-input-container"
      :class="{
        'gpx-input-container_error': hasErrors,
        'gpx-input-container_noBorder': noBorder,
      }"
    >
      <label class="gpx-input-label-container" :for="id || name">
        <input
          v-bind="$attrs"
          :id="id || name"
          ref="inputRef"
          :name="name"
          :value="modelValue || value"
          :disabled="disabled"
          :placeholder="placeholder"
          class="gpx-input"
          :class="{ 'gpx-input_disabled': disabled }"
          :type="contentHidden ? 'text' : type"
          :required="required"
          @input="onInput"
          @blur="handleBlur"
        >
        <span
          class="gpx-floating-label whitespace-nowrap"
          :class="{ 'gpx-floating-label_is-floating': isLabelFloating, 'gpx-floating-label_error': hasErrors }"
        >
          <span class="flex flex-row gap-1 items-center">
            <Icon v-if="labelIcon" :icon="labelIcon" />
            <span>{{ computedLabel }}</span>
          </span>
        </span>
      </label>
      <div
        v-if="clearable || type === 'password'"
        class="flex gap-2 items-center justify-center absolute inset-y-0 right-4 text-gray-500"
      >
        <button v-if="clearable && (value || modelValue)" type="button" data-test="gpx-input-clear-button" @click.prevent="onClear">
          <span class="sr-only">{{ t("input.clear") }} {{ label }}</span>
          <Icon icon="Close" size="sm" />
        </button>
        <button v-if="type === 'password'" type="button" @click.prevent="contentHidden = !contentHidden">
          <Icon :icon="contentHidden ? 'EyeOff' : 'EyeOn'" />
          <span class="sr-only">
            {{ !contentHidden ? t("hide_password_visibility") : t("show_password_visibility") }}
          </span>
        </button>
      </div>
    </div>
    <span v-if="hasErrors" class="gpx-field-error" v-text="error || errorMessage" />
    <Hint v-if="hint" :hint="hint" class="mt-1.5" />
  </div>
</template>
